import type { MutableRefObject } from "react";
import {
  usePaymentQuery,
  usePaymentMessageDraftQuery,
  useCustomerRepresentativeByPaymentIdQuery,
  usePaymentLatestThreadSubjectQuery,
} from "../api/queries";
import { usePaymentEscalationQuery } from "../api/queries/escalationQueries";
import { Loader, Center } from "@mantine/core";
import {
  IconCalendarWeek,
  IconFileDollar,
  IconArrowLeft,
  IconUser,
  IconGrid3x3,
} from "@tabler/icons-react";
import { MinimalCardList } from "./MinimalCard";
import { EmailComposer } from "./EmailComposer";
import { InvoiceEmailSendingToggle } from "./InvoiceEmailSendingToggle";
import { DraftFeedbackBar } from "./DraftFeedback/DraftFeedbackBar";
import { CorrespondenceSection } from "./CorrespondenceSection";
import { PaymentHistorySection } from "./PaymentHistorySection";
import { InvoiceNotesHistorySection } from "./InvoiceNotesHistorySection";
import { displayCurrencyAmount, displayISODate } from "../utils/display";
import { InvoiceEscalationSection } from "./InvoiceEscalationSection";
import { isDraftPendingReviewError } from "./draftErrors";
import { useAuth } from "../hooks/useAuth";

type InvoiceDetailPanelProps = {
  invoiceId: string;
  /** When set, shows a back control in the page header. */
  onBack?: () => void;
  onCustomerClick?: (customerId: number) => void;
  navigateOnSendSuccess?: boolean;
  embedded?: boolean;
  discardGuardRef?: MutableRefObject<(() => boolean) | null>;
};

export function InvoiceDetailPanel({
  invoiceId,
  onBack,
  onCustomerClick,
  navigateOnSendSuccess = true,
  embedded = false,
  discardGuardRef,
}: InvoiceDetailPanelProps) {
  const { isOrgManager, canMutateOrg } = useAuth();

  const {
    data: payment,
    isLoading: isPaymentLoading,
    isError: isPaymentError,
  } = usePaymentQuery(invoiceId, { enabled: Boolean(invoiceId) });

  const {
    data: emailDraft,
    isLoading: isMessageDraftLoading,
    isError: isMessageDraftError,
    error: messageDraftError,
  } = usePaymentMessageDraftQuery(invoiceId, { enabled: Boolean(invoiceId) });

  const { data: escalationState } = usePaymentEscalationQuery(invoiceId);

  const pendingApproval =
    Boolean(
      emailDraft?.requires_approval && emailDraft.approval_status === "pending",
    ) ||
    (isMessageDraftError && isDraftPendingReviewError(messageDraftError));

  const { data: latestThreadSubject, isLoading: isLatestThreadSubjectLoading } =
    usePaymentLatestThreadSubjectQuery(invoiceId, {
      enabled: Boolean(invoiceId),
    });

  const { data: customerRep, isLoading: isCustomerRepLoading } =
    useCustomerRepresentativeByPaymentIdQuery(invoiceId, {
      enabled: Boolean(invoiceId),
    });

  if (
    isPaymentLoading ||
    isCustomerRepLoading ||
    isMessageDraftLoading ||
    isLatestThreadSubjectLoading
  ) {
    return (
      <Center className={embedded ? "py-16" : "h-full pb-24"}>
        <Loader />
      </Center>
    );
  }

  if (isPaymentError || !payment) {
    return (
      <Center className={embedded ? "py-16 px-4" : "h-full pb-24 px-4"}>
        <div className="flex max-w-md flex-col items-center gap-4 text-center">
          <p className="text-lg font-medium text-slate-900">
            Invoice not found or you do not have access to it.
          </p>
          {onBack ? (
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
              onClick={onBack}
            >
              Go back
            </button>
          ) : null}
        </div>
      </Center>
    );
  }

  const customerName =
    customerRep?.customer_organization_name?.trim() ||
    String(payment.customer_organization_id ?? "Customer");
  const customerId = customerRep?.id ?? payment.customer_organization_id;

  const invoiceMinimalCardData = [
    {
      heading: "Customer",
      description: customerName,
      Icon: IconUser,
      key: 1,
      onClick:
        customerId && onCustomerClick
          ? () => onCustomerClick(customerId)
          : undefined,
      clickAriaLabel: `View customer ${customerName}`,
    },
    {
      heading: "Payment due",
      description: displayISODate(payment.due_date),
      Icon: IconCalendarWeek,
      key: 2,
    },
    {
      heading: "Amount",
      description: displayCurrencyAmount(payment.amount, payment.currency_type),
      Icon: IconFileDollar,
      key: 3,
    },
    {
      heading: "Invoice ID",
      description: payment.external_id ?? "",
      Icon: IconGrid3x3,
      key: 4,
    },
  ];

  return (
    <div
      className={
        embedded
          ? "flex w-full min-h-0 flex-col gap-4"
          : "mx-auto flex w-full max-w-6xl min-h-0 flex-col gap-4 pb-10"
      }
    >
      {!embedded ? (
        <header className="flex items-center gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Go back"
              className="shrink-0 rounded-lg border border-slate-200 bg-white p-2 text-slate-700 shadow-sm hover:bg-slate-50"
            >
              <IconArrowLeft size={20} />
            </button>
          ) : null}
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-xl font-semibold text-slate-900 sm:text-2xl">
              Invoice center
            </h1>
          </div>
        </header>
      ) : null}

      <MinimalCardList cardProps={invoiceMinimalCardData} />

      {escalationState ? (
        <InvoiceEscalationSection
          state={escalationState}
          draft={emailDraft ?? null}
          invoiceId={invoiceId}
        />
      ) : null}

      <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Compose email
          </h2>
          <InvoiceEmailSendingToggle
            invoiceId={invoiceId}
            isEmailSendingEnabled={payment.is_email_sending_enabled !== false}
          />
        </div>
        <EmailComposer
          emailDraft={emailDraft ?? null}
          customerEmail={customerRep?.email}
          invoiceId={invoiceId}
          latestThreadSubject={latestThreadSubject?.subject || null}
          language={customerRep?.language}
          composerDisabled={
            payment.is_email_sending_enabled === false ||
            (pendingApproval && !isOrgManager)
          }
          navigateOnSuccess={navigateOnSendSuccess}
          discardGuardRef={discardGuardRef}
        />
        {emailDraft?.uid && !pendingApproval && canMutateOrg ? (
          <DraftFeedbackBar draftUid={emailDraft.uid} />
        ) : null}
      </section>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <section className="min-w-0 flex-1">
          <CorrespondenceSection invoiceId={invoiceId} />
        </section>
        <aside className="w-full shrink-0 space-y-6 lg:w-80 xl:w-96">
          <PaymentHistorySection invoiceId={invoiceId} />
          {payment.customer_organization_id ? (
            <InvoiceNotesHistorySection
              invoiceId={invoiceId}
              customerOrgId={payment.customer_organization_id}
            />
          ) : null}
        </aside>
      </div>
    </div>
  );
}
